import * as React from "react";
import type { CreditCertainty } from "@zworks/console-contracts";
import { Badge, type BadgeProps } from "@/components/ui/badge";

export interface CertaintyBadgeProps extends Omit<BadgeProps, "children"> {
  certainty: CreditCertainty;
  label?: string;
}

const certaintyConfig: Record<
  CreditCertainty,
  { label: string; variant: NonNullable<BadgeProps["variant"]> }
> = {
  confirmed: { label: "Confirmed", variant: "success" },
  reserved: { label: "Reserved", variant: "info" },
  estimated: { label: "Estimated", variant: "warning" },
  unknown: { label: "Unknown", variant: "unknown" },
};

/**
 * CertaintyBadge conveys spend certainty across runs, usage, and ledger:
 * - Confirmed: Settled and reconciled
 * - Reserved: Held by running work
 * - Estimated: Projected before final reconciliation
 * - Unknown: Unreconciled; never shown as zero or dropped
 */
export function CertaintyBadge({ certainty, label, ...props }: CertaintyBadgeProps) {
  const config = certaintyConfig[certainty] ?? {
    label: String(certainty),
    variant: "unknown",
  };
  return (
    <Badge variant={config.variant} {...props}>
      {label ?? config.label}
    </Badge>
  );
}
